/*
 * BB Luxe Collection Swiper — carousel behaviour.
 *
 * Extracted from the section's inline <script> (2026-07-31) while fixing a
 * layout-thrashing bug: the old code read scrollWidth/clientWidth/offsetWidth
 * inside the scroll rAF callback and inside the autoplay tick, so every frame
 * of every smooth-scroll forced a synchronous re-layout. With two carousels on
 * the homepage that measured ~2.4s of forced style recalculation, and because
 * the autoplay interval never stopped, the page never reached main-thread idle
 * (Lighthouse reported NO_LCP / TBT errors as a result).
 *
 * Two rules keep it cheap now:
 *   1. Every layout read happens in measure(), which runs only when geometry
 *      can actually have changed (init, load, resize) — never per frame.
 *   2. Autoplay runs only while the carousel is on screen and the tab is
 *      visible, and stops for good once the visitor takes control.
 *
 * PERF (audit 2026-08-05): a homepage can legitimately carry more than one
 * instance of this section (this store runs two). Every instance used to be
 * fully initialised — computed-style reads, hover-image listeners, the
 * reveal IntersectionObserver, the autoplay IntersectionObserver — the
 * moment DOMContentLoaded fired, regardless of whether the carousel was
 * anywhere near the viewport. That's real, avoidable main-thread work
 * competing with LCP for every instance below the fold. initSection() is
 * now gated behind its own lightweight "about to be visible" observer, and
 * a matching shopify:section:unload handler tears each instance fully down
 * (observers disconnected, timers cleared, listeners removed) so editing
 * this section's settings in the theme customizer doesn't accumulate
 * detached-DOM listeners across edits.
 */
(function () {
  if (window.__blCsSwiperInit) return;
  window.__blCsSwiperInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // sec -> cleanup function, so a theme-editor section swap can fully tear
  // down the outgoing instance instead of leaking its listeners/observers.
  var cleanups = new WeakMap();

  function initSection(sec) {
    if (sec.__blCsReady) return;
    sec.__blCsReady = true;

    var track = sec.querySelector('.bl-cs__track');
    if (!track) return;

    var prev = sec.querySelector('.bl-cs-arrow--prev');
    var next = sec.querySelector('.bl-cs-arrow--next');
    var bar = sec.querySelector('.bl-cs__progress-bar');

    var teardowns = [];
    function onCleanup(fn) {
      teardowns.push(fn);
    }

    var rtl = getComputedStyle(track).direction === 'rtl';
    if (rtl) sec.classList.add('is-rtl');
    var sign = rtl ? -1 : 1;

    // --- cached geometry: the only place layout is read ---------------------
    var maxScroll = 0;
    var stepSize = 0;
    var barTravel = 0;

    function measure() {
      maxScroll = track.scrollWidth - track.clientWidth;
      var card = track.querySelector('.bl-cs-card');
      var gap = parseInt(getComputedStyle(track).gap) || 0;
      stepSize = card ? card.offsetWidth + gap : Math.round(track.clientWidth * 0.8);
      if (bar && bar.parentElement) {
        barTravel = Math.max(bar.parentElement.clientWidth - bar.offsetWidth, 0);
      }
      paint(true);
    }

    // --- painting: one cheap scrollLeft read, writes only on real change ----
    var ticking = false;
    var lastPct = -1;
    var lastAtStart = null;
    var lastAtEnd = null;

    function paint(force) {
      ticking = false;
      var pct = maxScroll > 0 ? Math.min(Math.abs(track.scrollLeft) / maxScroll, 1) : 0;
      if (!force && Math.abs(pct - lastPct) < 0.002) return;
      lastPct = pct;

      // translateX on an absolutely positioned bar is composited: it repaints
      // without dirtying layout, so the next frame's scrollLeft read is free.
      if (bar) bar.style.transform = 'translateX(' + pct * barTravel * sign + 'px)';

      // Toggling `disabled` invalidates style for the whole button subtree —
      // only touch it when the state actually flips.
      var atStart = pct <= 0.01;
      var atEnd = pct >= 0.99;
      if (prev && atStart !== lastAtStart) prev.toggleAttribute('disabled', atStart);
      if (next && atEnd !== lastAtEnd) next.toggleAttribute('disabled', atEnd);
      lastAtStart = atStart;
      lastAtEnd = atEnd;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          paint(false);
        });
      }
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    onCleanup(function () {
      track.removeEventListener('scroll', onScroll);
    });

    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    }
    window.addEventListener('resize', onResize, { passive: true });
    onCleanup(function () {
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
    });

    function onPrevClick() {
      track.scrollBy({ left: -stepSize * sign, behavior: 'smooth' });
    }
    function onNextClick() {
      track.scrollBy({ left: stepSize * sign, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', onPrevClick);
    if (next) next.addEventListener('click', onNextClick);
    onCleanup(function () {
      if (prev) prev.removeEventListener('click', onPrevClick);
      if (next) next.removeEventListener('click', onNextClick);
    });

    // Measure after first paint so init never forces layout mid-parse, then
    // again on load once lazy images have settled the track's scrollWidth.
    requestAnimationFrame(measure);
    if (document.readyState === 'complete') {
      setTimeout(measure, 0);
    } else {
      window.addEventListener('load', measure, { once: true });
      onCleanup(function () {
        window.removeEventListener('load', measure);
      });
    }

    // --- hover images: only fetch them on devices that can actually hover ----
    // Shipped as data-src by the section (see the comment there). Promoting on
    // pointerenter means a phone never downloads them; a mouse user gets the
    // image on the first hover, before the CSS opacity transition finishes.
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      var promote = function (card) {
        var alt = card.querySelector('.bl-cs-card__img--alt[data-src]');
        if (!alt) return;
        if (alt.dataset.srcset) alt.srcset = alt.dataset.srcset;
        alt.src = alt.dataset.src;
        alt.removeAttribute('data-src');
        alt.removeAttribute('data-srcset');
      };
      var onPointerEnter = function (event) {
        var card = event.target.closest ? event.target.closest('.bl-cs-card') : null;
        if (card) promote(card);
      };
      var onFocusIn = function (event) {
        var card = event.target.closest ? event.target.closest('.bl-cs-card') : null;
        if (card) promote(card);
      };
      track.addEventListener('pointerenter', onPointerEnter, { capture: true, passive: true });
      // Keyboard users never fire pointerenter.
      track.addEventListener('focusin', onFocusIn, { passive: true });
      onCleanup(function () {
        track.removeEventListener('pointerenter', onPointerEnter, { capture: true });
        track.removeEventListener('focusin', onFocusIn);
      });
    }

    // --- reveal animation ---------------------------------------------------
    if (!reduceMotion && 'IntersectionObserver' in window) {
      sec.classList.add('bl-cs--anim');
      var items = sec.querySelectorAll('.bl-cs-reveal');
      var revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              e.target.classList.add('is-in');
              revealObserver.unobserve(e.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      items.forEach(function (el, i) {
        el.style.transitionDelay = Math.min(i * 60, 320) + 'ms';
        revealObserver.observe(el);
      });
      onCleanup(function () {
        revealObserver.disconnect();
      });
      // Fail open: if the observer never fires, show everything anyway.
      var revealFailOpen = setTimeout(function () {
        items.forEach(function (el) {
          el.classList.add('is-in');
        });
      }, 1600);
      onCleanup(function () {
        clearTimeout(revealFailOpen);
      });
    }

    // --- autoplay -----------------------------------------------------------
    if (sec.dataset.autoplay !== 'true' || reduceMotion) {
      cleanups.set(sec, function () {
        teardowns.forEach(function (fn) {
          fn();
        });
      });
      return;
    }

    var delay = parseInt(sec.dataset.autoplayDelay, 10) || 4000;
    var timer = null;
    var inView = false;
    var hovered = false;
    var stopped = false;

    function tick() {
      if (document.hidden || !inView) return;
      // Cached maxScroll/stepSize — no layout read on the interval.
      if (Math.abs(track.scrollLeft) >= maxScroll - 4) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: stepSize * sign, behavior: 'smooth' });
      }
    }

    function start() {
      if (stopped || timer || !inView || document.hidden || hovered) return;
      timer = setInterval(tick, delay);
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    // Once the visitor drives the carousel themselves, leave it where they put it.
    function halt() {
      stopped = true;
      stop();
    }

    ['pointerdown', 'touchstart', 'keydown'].forEach(function (ev) {
      track.addEventListener(ev, halt, { passive: true, once: true });
    });
    if (prev) prev.addEventListener('click', halt);
    if (next) next.addEventListener('click', halt);
    onCleanup(function () {
      if (prev) prev.removeEventListener('click', halt);
      if (next) next.removeEventListener('click', halt);
    });

    function onHoverEnter() {
      hovered = true;
      stop();
    }
    function onHoverLeave() {
      hovered = false;
      start();
    }
    ['pointerenter', 'focusin'].forEach(function (ev) {
      track.addEventListener(ev, onHoverEnter, { passive: true });
    });
    ['pointerleave', 'focusout'].forEach(function (ev) {
      track.addEventListener(ev, onHoverLeave);
    });
    onCleanup(function () {
      ['pointerenter', 'focusin'].forEach(function (ev) {
        track.removeEventListener(ev, onHoverEnter);
      });
      ['pointerleave', 'focusout'].forEach(function (ev) {
        track.removeEventListener(ev, onHoverLeave);
      });
    });

    function onVisibilityChange() {
      if (document.hidden) stop();
      else start();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    onCleanup(function () {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    if ('IntersectionObserver' in window) {
      var autoplayObserver = new IntersectionObserver(
        function (entries) {
          inView = entries[0].isIntersecting;
          if (inView) start();
          else stop();
        },
        { threshold: 0.2 }
      );
      autoplayObserver.observe(sec);
      onCleanup(function () {
        autoplayObserver.disconnect();
      });
    } else {
      inView = true;
      start();
    }

    onCleanup(stop);
    cleanups.set(sec, function () {
      teardowns.forEach(function (fn) {
        fn();
      });
    });
  }

  // Lazy init: don't do any of the work above until a carousel is actually
  // about to be scrolled into view. rootMargin gives it a head start so the
  // carousel is fully interactive (arrows, progress bar, autoplay) well
  // before it reaches the viewport, without paying that cost for instances
  // the visitor may never scroll to.
  var lazyObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                lazyObserver.unobserve(entry.target);
                initSection(entry.target);
              }
            });
          },
          { rootMargin: '600px 0px 600px 0px', threshold: 0 }
        )
      : null;

  function watch(sec) {
    if (sec.__blCsReady || sec.__blCsWatched) return;
    sec.__blCsWatched = true;
    if (lazyObserver) {
      lazyObserver.observe(sec);
    } else {
      initSection(sec);
    }
  }

  function initAll() {
    document.querySelectorAll('.bl-cs').forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor / section re-render support.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target instanceof Element) {
      var sections = event.target.matches('.bl-cs') ? [event.target] : event.target.querySelectorAll('.bl-cs');
      sections.forEach(watch);
    } else {
      initAll();
    }
  });

  // Tear down fully when the theme editor swaps this section's markup out —
  // otherwise the outgoing instance's window/document-level listeners and
  // observers (resize, visibilitychange, autoplay IntersectionObserver)
  // outlive the detached DOM they close over.
  document.addEventListener('shopify:section:unload', function (event) {
    if (!(event.target instanceof Element)) return;
    var sections = event.target.matches('.bl-cs') ? [event.target] : Array.prototype.slice.call(event.target.querySelectorAll('.bl-cs'));
    sections.forEach(function (sec) {
      if (lazyObserver) lazyObserver.unobserve(sec);
      var cleanup = cleanups.get(sec);
      if (cleanup) {
        cleanup();
        cleanups.delete(sec);
      }
    });
  });
})();
